import { useEffect } from 'react';
import { Point } from '@/types';

type OnArrowDownCallback = (delta: Point) => void;
type OnArrowUpCallback = () => void;
type OnDeleteCallback = (event: KeyboardEvent) => void;

const getKeyCode = (k: KeyboardEvent) => k.keyCode; // deprecated property is the only option until IE11 gets dropped

const handleKeyboardEvents = () =>
  !(document.activeElement instanceof HTMLTextAreaElement) &&
  !(document.activeElement instanceof HTMLInputElement);

const getKeyDownHandler =
  (onArrowDownCallback: OnArrowDownCallback, onDeleteCallback: OnDeleteCallback) =>
  (k: KeyboardEvent) => {
    if (!handleKeyboardEvents()) {
      return;
    }
    const keyCode = getKeyCode(k);
    if (keyCode >= 37 && keyCode <= 40) {
      const stepSize = k.shiftKey ? 10 : 1;
      const delta = { x: 0, y: 0 };
      if (keyCode === 37) {
        delta.x = -stepSize;
      } else if (keyCode === 39) {
        delta.x = stepSize;
      } else if (keyCode === 38) {
        delta.y = -stepSize;
      } else if (keyCode === 40) {
        delta.y = stepSize;
      }
      onArrowDownCallback(delta);

      k.preventDefault();
    } else if (keyCode === 8 || keyCode === 46) {
      onDeleteCallback(k);
    }
  };

const getKeyUpHandler = (onArrowUpCallback: () => void) => (k: KeyboardEvent) => {
  if (!handleKeyboardEvents()) {
    return;
  }
  const keyCode = getKeyCode(k);
  if (keyCode >= 37 && keyCode <= 40) {
    onArrowUpCallback();
  }
};

export function useKeyboardHandler(
  isActive: boolean,
  onArrowDownCallback: OnArrowDownCallback,
  onArrowUpCallback: OnArrowUpCallback,
  onDeleteCallback: OnDeleteCallback
) {
  useEffect(() => {
    if (isActive) {
      const keyDownHandler = getKeyDownHandler(onArrowDownCallback, onDeleteCallback);
      const keyUpHandler = getKeyUpHandler(onArrowUpCallback);
      document.addEventListener('keydown', keyDownHandler);
      document.addEventListener('keyup', keyUpHandler);
      return () => {
        document.removeEventListener('keydown', keyDownHandler);
        document.removeEventListener('keyup', keyUpHandler);
      };
    }
  }, [onArrowDownCallback, onArrowUpCallback, onDeleteCallback, isActive]);
}
